import { IMAGES } from "@/config/site";

// Add a new initiative by appending an object here. Only list programs that are operational.
// status: "active" shows on the Programs page; "future" shows under Future Programs (once approved).
export const PROGRAMS = [
  {
    id: "service-dog-program",
    status: "active",
    name: "Service Dog Program",
    tagline: "Featured initiative",
    summary:
      "In partnership with Big Dog Ranch Rescue, supporting eligible active and retired first responders and military veterans in Palm Beach and Broward counties through carefully evaluated, professionally trained dogs. Free to approved participants.",
    image: IMAGES.luna,
    to: "/service-dog-program",
    cta: "Explore the Program",
  },
  {
    id: "canteen-33",
    status: "active",
    name: "Canteen 33",
    tagline: "Mobile canteen and nonmedical rehabilitation support",
    summary:
      "The Foundation's mobile canteen serving Palm Beach County. When requested by a public safety agency, Canteen 33 provides water, electrolyte beverages, coffee, food, and nonmedical support to crews working extended incidents.",
    image: IMAGES.fireScene,
    to: "/programs#canteen-33",
    cta: "Learn About Canteen 33",
  },
  {
    id: "lifeline-assistance",
    status: "coming-soon",
    name: "First Responders Lifeline Assistance Program",
    tagline: "Emergency Financial Assistance & Family Support",
    summary:
      "A program under development to provide short-term emergency financial assistance to eligible first responders and their families facing hardship caused by serious illness, injury, or death, on or off duty. Not yet accepting applications or providing assistance.",
    to: "/programs#lifeline-assistance",
    cta: "Learn More",
  },
];

export const SERVICE_DOG_FAQ = [
  {
    q: "Who may apply?",
    a: "Active and retired firefighters, law enforcement officers, EMS personnel, dispatchers, and military veterans who live in Palm Beach or Broward County may apply.",
  },
  {
    q: "Is there a cost to participate?",
    a: "The program is free to approved participants. Participants are responsible for food, routine veterinary care, and other ongoing expenses of dog ownership.",
  },
  {
    q: "Can I apply with my own dog?",
    a: "Yes. You may request an evaluation of your existing dog. Big Dog Ranch Rescue must evaluate and approve the dog before it can enter the training program. Approval is not guaranteed.",
  },
  {
    q: "How are dogs evaluated?",
    a: "Big Dog Ranch Rescue evaluates and approves every dog in the program, including dogs that applicants already own, and oversees training, placement, and follow-up.",
  },
  {
    q: "What happens after I submit an application?",
    a: "First Responders Foundation reviews your application for completeness and basic eligibility. Applications that meet the initial requirements are referred to Big Dog Ranch Rescue, which makes the final decision regarding approval.",
  },
  {
    q: "How long does the process take?",
    a: "Timelines vary depending on the applicant, the dog, and the training involved. Expected timelines are discussed with applicants as they move through the process.",
  },
  {
    q: "Is acceptance guaranteed?",
    a: "No. Submitting an application does not guarantee approval, placement, completion of training, or service dog certification. Big Dog Ranch Rescue makes the final decision regarding applicant and dog approval.",
  },
  {
    q: "How can I support the program?",
    a: "You can give to the Service Dog Program through Zeffy, become a sponsor, contribute approved in-kind items, or volunteer your time.",
  },
];

// Luna's approved biography. Add more photos to the gallery by adding entries from IMAGES or new { src, alt } objects.
export const LUNA = {
  bio: [
    "Luna was adopted by First Responders Foundation from Big Dog Ranch Rescue. She is a mixed-breed dog who was 4 years and 3 months old when she joined the Foundation, and she will turn five in December 2026.",
    "Luna is a quick learner who loves people and other dogs. She is still in training and doing great.",
  ],
  facts: [
    { k: "Adopted from", v: "Big Dog Ranch Rescue" },
    { k: "Breed", v: "Mixed breed" },
    { k: "Age at adoption", v: "4 years, 3 months" },
    { k: "Turns five", v: "December 2026" },
    { k: "Status", v: "In training" },
  ],
  gallery: [IMAGES.luna, IMAGES.lunaResting],
};

// Leadership. Add a photo by setting photo: "/images/name.jpg"; null shows "Photo coming soon".
export const LEADERSHIP = [
  {
    name: "Dwight Saxon",
    title: "Founder & President",
    bio: "Retired Palm Beach County Fire Rescue professional with 30 years of fire service and a United States Army veteran with six years of military service. Dwight founded First Responders Foundation to support the well-being of first responders and strengthen community partnerships.",
    photo: "/images/leader-dwight-saxon.jpg",
    position: "50% 30%",
  },
  {
    name: "Iva Grady",
    title: "Treasurer",
    bio: "Retired Palm Beach County administrator who helped establish the Palm Beach Marathon. Iva brings extensive public service experience and community involvement to the Foundation.",
    photo: "/images/leader-iva-grady.jpg",
    position: "50% 40%",
  },
  {
    name: "Greg Rice",
    title: "Secretary",
    bio: "Retired marketing professional with Hulett Environmental Services and a longtime Palm Beach County resident. Greg contributes marketing experience, community knowledge, and relationship-building skills.",
    photo: "/images/leader-greg-rice.jpg",
    position: "35% 15%",
  },
];

// Canteen 33 deployment photographs.
export const CANTEEN_PHOTOS = [
  { src: "/images/scene-pbcfr-engine-36.jpg", alt: "Greenacres Fire Rescue Fire Prevention and Palm Beach County Sheriff's Office vehicles parked on a residential street during an incident", caption: "Canteen 33 on a deployment with Greenacres Fire Rescue Fire Prevention and the Palm Beach County Sheriff's Office." },
  { src: "/images/scene-greenacres-crews-rehab.jpg", alt: "Greenacres Fire Rescue firefighters with equipment staged on a lawn beside their engine", caption: "Greenacres Fire Rescue firefighters and equipment staged on scene during a Canteen 33 deployment." },
  { src: "/images/scene-fire-prevention-sheriff.jpg", alt: "Palm Beach County Fire Rescue Engine 36 and Greenacres Fire Rescue units parked at an incident", caption: "Palm Beach County Fire Rescue Engine 36 and Greenacres Fire Rescue units on scene during a Canteen 33 deployment." },
];

// Frank Turner memorial. Add a photo by setting src and alt on an entry; entries without src show "Photo coming soon".
export const FRANK_TURNER = {
  title: "Remembering Frank Turner — A Legacy of Giving",
  story: [
    "Frank Turner worked for the Internal Revenue Service and was not a firefighter, yet for more than 20 years he gave his time to our community in a way few people ever will. Each holiday season, Frank volunteered as Santa Claus aboard a historic 1961 ladder truck purchased by firefighters Dwight Saxon and Buddy Yarborough, both members of the Palm Beach County FOOLS firefighter organization.",
    "Frank helped bring Christmas joy to children and families, including children spending the holidays in the hospital.",
    "After Frank was diagnosed with ALS (Lou Gehrig's disease), First Responders Foundation organized fundraising activities to support Frank and his wife, Becky Turner. The primary fundraiser was held at Drive Shack, and a gathering at Lindburgers included a 50/50 raffle and auction items. Together, the events raised approximately $8,000. Working with Firefighters on a Mission, the Foundation also helped secure a used wheelchair-accessible van for Frank and Becky.",
    "Frank has since passed away. We remember him for his generosity, his years of volunteer service, and the joy he brought to so many children, and we are grateful to everyone who came together to support Frank and Becky.",
  ],
  facts: [
    { k: "Years as Santa", v: "20+" },
    { k: "Historic ladder truck", v: "1961" },
    { k: "Raised for Frank & Becky", v: "About $8,000" },
  ],
  photos: [
    { src: "/images/frank-check-presentation.jpg", alt: "Connie Saxon, Becky Turner, and a Lindburgers bartender holding a large presentation check", caption: "Left to right: Connie Saxon, Becky Turner, and a Lindburgers bartender.", position: "50% 30%" },
    { src: "/images/frank-santa-ladder-1.jpg", alt: "Frank Turner dressed as Santa Claus aboard the historic 1961 ladder truck", caption: "Frank Turner as Santa Claus aboard the historic 1961 ladder truck.", position: "50% 30%" },
    { src: "/images/frank-driveshack-2.jpg", alt: "Supporters gathered on lounge seating at the Drive Shack fundraiser", caption: "Supporters gathered at the Drive Shack fundraiser for Frank and Becky Turner." },
    { src: "/images/frank-driveshack-3.jpg", alt: "A supporter taking a swing in a hitting bay at Drive Shack", caption: "Taking a swing for Frank at the Drive Shack fundraiser." },
    { src: "/images/frank-lindburgers-group.jpg", alt: "A group of supporters posing under the Lindburgers sign", caption: "Friends and supporters at the Lindburgers gathering." },
    { src: "/images/frank-lindburgers-gathering.jpg", alt: "Guests seated and talking at tables inside Lindburgers", caption: "The gathering at Lindburgers, with a 50/50 raffle and auction items." },
    { src: "/images/frank-driveshack-1.jpg", alt: "Three supporters smiling and giving a thumbs-up at Drive Shack", caption: "All smiles at the Drive Shack fundraiser." },
  ],
};
